import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, CardBody, Input } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { formatPhase3PriceTagMoney, getPhase3ErrorMessage, normalizePhase3ImageUrl } from '../helpers/phase3-inbound-journey-helper'

const ITEMS_PER_PAGE = 12

const PriceTagCard = ({ item }) => {
  if (!item) return <div className='phase3-price-tag-card empty' />

  return (
    <div className='phase3-price-tag-card'>
      { item.is_hermes_bag ? (
        <div className='phase3-price-tag-header centered'>
          <img src='/price_tag_header.png' alt='Belle and Kate Logo' />
        </div>
      ) : (
        <div className='phase3-price-tag-header'>
          <img src='/price_tag_header.png' alt='Belle and Kate Logo' />
          { item.designer_price_tag_logo_url && <img className='brand-logo' src={ normalizePhase3ImageUrl(item.designer_price_tag_logo_url) } alt='Brand Logo' /> }
        </div>
      ) }

      <div className='phase3-price-tag-sku'>{ item.sku || '-' }</div>

      <div className='phase3-price-tag-grid'>
        <div className='phase3-price-tag-box name'>{ item.name || 'N/A' }</div>
        <div className='phase3-price-tag-box comes-with'>
          <div>COMES WITH:</div>
          <div>{ item.comes_with || 'N/A' }</div>
        </div>
        <div className='phase3-price-tag-image'>
          <img src={ normalizePhase3ImageUrl(item.image_url) || 'https://placehold.co/500x500' } alt={ item.name || '' } />
        </div>
        <div className='phase3-price-tag-box condition'>
          <div>CONDITION/NOTE:</div>
          <div>{ [ item.condition?.name?.toUpperCase(), item.condition?.default_message ].filter(Boolean).join(': ') || 'N/A' }</div>
        </div>
      </div>

      <div className='phase3-price-tag-price'>{ formatPhase3PriceTagMoney(item.price) }</div>
    </div>
  )
}

const Phase3PriceTagPrintPage = ({ apiNamespace = 'inbound' }) => {
  const [ searchTerms, setSearchTerms ] = useState(new Array(ITEMS_PER_PAGE).fill(''))
  const [ items, setItems ] = useState(new Array(ITEMS_PER_PAGE).fill(null))
  const [ errorList, setErrorList ] = useState(new Array(ITEMS_PER_PAGE).fill(''))
  const itemsRef = useRef(items)
  const lastPrintAuditAtRef = useRef(0)

  useEffect(() => {
    itemsRef.current = items
  }, [ items ])

  useEffect(() => {
    const auditPrintDialog = (triggerSource) => {
      const products = itemsRef.current
        .map((item, index) => item ? { product_id: item.id, slot_number: index + 1 } : null)
        .filter(Boolean)

      if (products.length === 0) return

      const now = Date.now()
      if (now - lastPrintAuditAtRef.current < 5000) return
      lastPrintAuditAtRef.current = now

      InboundJourneyApiV2.priceTagAudit({
        event: 'print_dialog_opened',
        products,
        triggerSource,
        apiNamespace
      }).catch((error) => {
        console.warn('Failed to write Price Tag print audit log.', error)
      })
    }

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'p') {
        auditPrintDialog('keyboard_shortcut')
      }
    }

    const handleBeforePrint = () => auditPrintDialog('browser_beforeprint')

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('beforeprint', handleBeforePrint)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('beforeprint', handleBeforePrint)
    }
  }, [ apiNamespace ])

  const setSearchTerm = (index, value) => {
    setSearchTerms((prev) => {
      const next = [ ...prev ]
      next[index] = value
      return next
    })

    setErrorList((prev) => {
      const next = [ ...prev ]
      next[index] = ''
      return next
    })
  }

  const setItemAt = (index, value) => {
    setItems((prev) => {
      const next = [ ...prev ]
      next[index] = value
      return next
    })
  }

  const setErrorAt = (index, value) => {
    setErrorList((prev) => {
      const next = [ ...prev ]
      next[index] = value
      return next
    })
  }

  const validateFullSku = (query) => {
    const skuParts = query.split('-').filter(Boolean)

    if (!query || skuParts.length < 3) {
      return 'Mohon masukkan SKU lengkap, contoh BKA-HR01-110726-96544. Jangan hanya masukkan 5 digit terakhir.'
    }

    return ''
  }

  const loadPriceTag = async (index) => {
    const query = searchTerms[index].trim()
    const validationMessage = validateFullSku(query)

    if (validationMessage) {
      setItemAt(index, null)
      setErrorAt(index, validationMessage)
      return
    }

    setErrorAt(index, '')

    try {
      const previousItem = items[index]
      const auditAction = previousItem ? (previousItem.sku.toUpperCase() === query.toUpperCase() ? 'reload' : 'replace') : 'add'
      const response = await InboundJourneyApiV2.priceTagData({
        q: query,
        auditAction,
        slotNumber: index + 1,
        previousSku: previousItem?.sku || '',
        apiNamespace
      })
      const data = response.data?.data

      if (!data) {
        setItemAt(index, null)
        setErrorAt(index, 'Data price tag tidak ditemukan.')
        return
      }

      setItemAt(index, data)
    } catch (error) {
      setItemAt(index, null)
      setErrorAt(index, getPhase3ErrorMessage(error))
    }
  }

  const removeItem = (index) => {
    const item = items[index]

    if (item) {
      InboundJourneyApiV2.priceTagAudit({
        event: 'remove',
        products: [{ product_id: item.id, slot_number: index + 1 }],
        apiNamespace
      }).catch((error) => {
        console.warn('Failed to write Price Tag remove audit log.', error)
      })
    }

    setItemAt(index, null)
    setErrorAt(index, '')
  }

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12.7mm 0; }
          html, body { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; }
          body { background: #fff !important; overflow: visible !important; zoom: 1 !important; }
          #root { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; }
          .phase3-print-hidden { display: none !important; }
          .main-content { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; transform: none !important; }
          .navbar, .sidenav, .backdrop { display: none !important; }
          .container-fluid { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; padding: 0 !important; margin: 0 !important; }
          .phase3-price-tag-layout { display: block !important; width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; }
          .phase3-price-tag-sheet { width: 157.427mm !important; min-width: 157.427mm !important; height: 271.463mm !important; margin: 0 auto !important; break-inside: avoid !important; page-break-inside: avoid !important; }
          .phase3-price-tag-card { width: 51.329mm !important; height: 65.617mm !important; break-inside: avoid !important; page-break-inside: avoid !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .phase3-price-tag-layout { display: flex; align-items: flex-start; justify-content: space-around; gap: 24px; }
        .phase3-price-tag-form { max-width: 620px; width: 48%; }
        .phase3-price-tag-input-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .phase3-price-tag-input-cell { border: 1px solid #000; border-radius: 0; min-height: 146px; padding: 8px; text-align: center; }
        .phase3-price-tag-input-cell .phase3-price-tag-help { color: #6b7280; font-size: 12px; line-height: 1.25; margin: 6px 0 4px; }
        .phase3-price-tag-input-cell .phase3-price-tag-error { color: #dc2626; font-size: 12px; font-weight: 700; line-height: 1.25; margin: 12px 0 0; }
        .phase3-price-tag-sheet { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-template-rows: repeat(4, minmax(0, 1fr)); width: 595px; min-width: 595px; height: 1026px; }
        .phase3-price-tag-card { width: 194px; height: 248px; padding: 6px; border: 1px solid #000; background: #fff; text-align: center; display: flex; flex-direction: column; overflow: hidden; }
        .phase3-price-tag-card.empty { border-color: transparent; }
        .phase3-price-tag-header { display: flex; justify-content: space-between; align-items: center; min-height: 28px; }
        .phase3-price-tag-header.centered { justify-content: center; }
        .phase3-price-tag-header img { max-height: 18px; object-fit: contain; }
        .phase3-price-tag-header img.brand-logo { max-height: 28px; max-width: 80px; }
        .phase3-price-tag-sku { font-weight: bold; font-size: 10px; line-height: 12px; }
        .phase3-price-tag-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(6, minmax(0, 1fr)); gap: 3px; height: 155px; text-align: left; }
        .phase3-price-tag-box { border: 1px solid #aaa; padding: 2px; overflow: hidden; font-size: 6px; line-height: 7px; }
        .phase3-price-tag-box.name { grid-row: span 2; display: flex; align-items: center; justify-content: center; text-align: center; font-weight: bold; }
        .phase3-price-tag-box.comes-with { grid-row: span 3; }
        .phase3-price-tag-image { grid-row: span 4; overflow: hidden; display: flex; align-items: center; justify-content: center; }
        .phase3-price-tag-image img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .phase3-price-tag-box.condition { grid-row: span 3; }
        .phase3-price-tag-price { margin-top: auto; font-weight: bold; font-size: 12px; }
      `}</style>

      <div className='phase3-print-hidden'>
        <TitlePage mainTitle='Inbound Journey' subTitle='Price Tag Print' />
      </div>

      <div className='phase3-price-tag-layout'>
        <div className='phase3-print-hidden phase3-price-tag-form'>
          <Card>
            <CardBody>
              <h4>Product Price Tag</h4>
              <ul className='small'>
                <li>Mohon isi sku dan klik Add. Meng-klik Remove akan menghilangkan data untuk satu cell tersebut.</li>
                <li>Untuk print, silahkan klik Ctrl+P atau Cmd+P di Mac, lalu print di kertas A4.</li>
                <li>Add, Reload, Remove, dan pembukaan Print dialog akan dicatat di Audit Log.</li>
                <li>Me-refresh halaman akan menghilangkan seluruh data dibawah ini.</li>
              </ul>

              <div className='phase3-price-tag-input-grid'>
                { Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <div key={ index } className='phase3-price-tag-input-cell'>
                    <Input
                      bsSize='sm'
                      placeholder='Contoh: BKA-HR01-110726-96544'
                      value={ searchTerms[index] }
                      onChange={ (event) => setSearchTerm(index, event.target.value) }
                    />
                    <p className='phase3-price-tag-help'>
                      { 'Gunakan SKU lengkap, contoh: BKA-HR01-110726-96544. Jangan hanya masukkan 5 digit terakhir.' }
                    </p>
                    <Button size='sm' color='link' className='p-0 mr-2' onClick={ () => loadPriceTag(index) }>
                      { items[index] ? 'Reload' : 'Add' }
                    </Button>
                    <Button size='sm' color='link' className={ items[index] ? 'p-0' : 'p-0 invisible' } onClick={ () => removeItem(index) }>
                      Remove
                    </Button>
                    {
                      errorList[index] ? (
                        <p className='phase3-price-tag-error'>{ errorList[index] }</p>
                      ) : null
                    }
                  </div>
                )) }
              </div>
            </CardBody>
          </Card>
        </div>

        <div className='phase3-price-tag-sheet'>
          { items.filter((item) => item).map((item, index) => <PriceTagCard key={ index } item={ item } />) }
        </div>
      </div>
    </>
  )
}

export default Phase3PriceTagPrintPage
